import * as React from 'react';
import { CircularProgress, TextField } from '@mui/material' ;
import { Autocomplete } from '@mui/material';
import { sleep } from '../../utils/utils';

export interface AsyncPickerProps<T> {
    getOptions: (parameter: string) => Promise<T[]>;
    getOptionLabel: (option: T) => string;
    getOptionKey: (option: T) => any;
    onValueChanged: (option: T | null | string) => void;
    disabled: boolean;
    placeholder?: string;
    freeInput?: true | undefined;
    value?: T | null | string;
}

export interface AsyncPickerState<T> {
    options: T[];
    loading: boolean;
    disabled: boolean;
    value?: T | null | string;
}

export default class AsyncPicker<T> extends React.Component<AsyncPickerProps<T>, AsyncPickerState<T>> {
    private currentSearchValue: string = '';

    constructor(props: AsyncPickerProps<T>) {
        super(props);

        this.state = {
            options: [],
            loading: false,
            disabled: props.disabled,
            value: props.value
        };
    }

    componentDidUpdate(prevProps: AsyncPickerProps<T>) {
        if (this.props.disabled !== prevProps.disabled) {
            this.setState({ disabled: this.props.disabled });
        }
        if (this.props.value !== prevProps.value) {
            this.setState({ value: this.props.value });
        }
    }

    private async loadOptions(value: string): Promise<void> {
        if (!value) {
            this.setState({ options: [] });
            return;
        }

        this.setState({ loading: true });
        try {
            const options = await this.props.getOptions(value);
            this.setState({ options: options, loading: false });
        } catch (e) {
            this.setState({ options: [], loading: false });
        }
    }

    private async onChange(value: string): Promise<void> {
        this.currentSearchValue = value;
        await sleep(500);
        if (this.currentSearchValue == value) {
            this.props.onValueChanged(value);
            await this.loadOptions(value);
        }
    }

    public render() {
        return (
            <React.Fragment>
                <Autocomplete
                    onInputChange={async (event, value, reason) => await this.onChange(!value || value == 'undefined' || value == 'null' ? '' : value)}
                    onChange={(e, option: T | null | string) => this.props.onValueChanged(option)}
                    getOptionSelected={(option: T, value: T) => this.props.getOptionKey(option) == this.props.getOptionKey(value)}
                    getOptionLabel={(option: string | T) => this.props.getOptionLabel(option as T)}
                    options={this.state.options}
                    loading={this.state.loading}
                    freeSolo={this.props.freeInput}
                    disabled={this.state.disabled}
                    className="form-control-input"
                    placeholder={this.props.placeholder}
                    value={this.state.value}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={this.props.placeholder}    
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <React.Fragment>
                                        {this.state.loading ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </React.Fragment>
                                ),
                            }}
                        />
                    )}
                />
            </React.Fragment >
        );
    }
}